export interface CanvasLogicalSize {
  width: number;
  height: number;
  profile: 'mobile' | 'mobile-wide' | 'tablet' | 'desktop';
}

/**
 * Stable render surfaces for the three responsive layouts. Browser zoom may
 * change CSS pixels, but it does not continuously rebuild the game world.
 */
export function getCanvasLogicalSize(viewportWidth: number): CanvasLogicalSize {
  const width = Number.isFinite(viewportWidth) ? viewportWidth : 1280;
  // Each surface has the same aspect ratio as its CSS box (.canvas-viewport):
  // phones 4:3, tablet and desktop 3:2. A mismatch stretches every sprite.
  // Phones up to the widest iPhone (and the iPhone Duo's outer display) use the
  // 375 px world. Wider single-column screens, such as the Duo opened (about
  // 669 pt), get a larger 4:3 world: stretching 375 px that far (1.78x) made
  // the skiers, labels and multiplier text crowd and run off the slope.
  if (width <= 480) return { width: 375, height: 281, profile: 'mobile' };
  if (width <= 760) return { width: 560, height: 420, profile: 'mobile-wide' };
  if (width <= 1180) return { width: 720, height: 480, profile: 'tablet' };
  return { width: 960, height: 640, profile: 'desktop' };
}

/**
 * Sprites (skiers, trees, rocks) are authored in world pixels for the 720–960 px
 * worlds, while the slope itself scales with the canvas (SLOPE_CONFIG.widthRatio).
 * On the 375 px phone world that made a tree a third of the slope, so sprites are
 * drawn smaller there. Text keeps its size: it is already at the legibility floor.
 */
export const PHONE_SPRITE_SCALE = 0.62;

export function spriteScaleFor(canvasWidth: number): number {
  if (canvasWidth <= 400) return PHONE_SPRITE_SCALE;
  if (canvasWidth >= 720) return 1;
  // Between the phone and tablet worlds (the 560 px wide-phone world), keep
  // sprites in proportion to the slope by easing from the phone scale to 1.
  return PHONE_SPRITE_SCALE + (1 - PHONE_SPRITE_SCALE) * (canvasWidth - 400) / (720 - 400);
}
